import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import WifiOffIcon from '@material-ui/icons/WifiOff';

import * as StoreService from '../../api/storeService';
import * as ProfileService from '../../api/profileService';
import * as EmploymentService from '../../api/employmentService';
import * as RestrictionService from '../../api/restrictionService';
import * as StampCampaignService from '../../api/stampCampaignService';
import useAuthContext from '../../hooks/useAuthContext';
import useNavContext from '../../hooks/useNavContext';
import useSelectedStoreContext from '../../hooks/useSelectedStoreContext';
import QRCodeOnlyScreen from '../../components/Screen/QRCodeOnlyScreen';
import SaveToHomeScreenDialog from '../../components/Dialog/SaveToHomeScreenDialog';
import SelectStoreDialog from '../../components/Dialog/SelectStoreDialog';
import Spinner from '../../components/Spinner';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  grow: {
    flexGrow: 1,
  },
  noNetworkContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  noNetworkHeader: {
    marginBottom: theme.spacing(),
  },
  noNetworkSubText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 16,
    textAlign: 'center',
  },
  wifiOff: {
    width: 150,
    height: 150,
  },
}));

/**
 * QR Code Mode screen
 */
export default function QRCodeMode() {
  const classes = useStyles();
  const selectedStore = useSelectedStoreContext();
  const [saveToHomeDialog, setSaveToHomeDialog] = useState(false);
  const [selectStoreDialogOpen, setSelectStoreDialogOpen] = useState(
    !selectedStore?.selectedStore?.storeId,
  );
  const [profile, setProfile] = useState<ProfileService.Profile | null>(null);
  const [employment, setEmployment] =
    useState<EmploymentService.Employment | null>(null);
  const [restrictedStamp, setRestrictedStamp] = useState(1);
  const [maxStampIssued, setMaxStampIssued] = useState(1);
  const [stampCampaignList, setStampCampaignList] = useState<
    StampCampaignService.StampCampaign[]
  >([]);
  const [init, setInit] = useState(true);
  const [profileFetchFailed, setProfileFetchFailed] = useState(false);
  const [employmentFetchFailed, setEmploymentFetchFailed] = useState(false);
  const [restrictionFetchFailed, setRestrictionFetchFailed] = useState(false);
  const auth = useAuthContext();
  const nav = useNavContext();
  const history = useHistory();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await ProfileService.get();
        setProfile(res);
        setProfileFetchFailed(false);
      } catch (e) {
        console.log('Error: ', e);
        setProfileFetchFailed(true);
      }
    };

    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await EmploymentService.get();
        if (res) {
          setEmployment(res);
          setEmploymentFetchFailed(false);
        }
      } catch (e) {
        console.log('Error:', e);
        setEmploymentFetchFailed(true);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetchRestrictionList = async () => {
      if (employment) {
        try {
          const res = await RestrictionService.listEmployeeRestriction(
            employment.company.id,
            employment.id,
          );
          const resStampCampaignList = await StampCampaignService.listActive(
            employment.company.id,
          );
          if (res && resStampCampaignList) {
            setStampCampaignList(resStampCampaignList);
            setRestrictionFetchFailed(false);
            const restrictionStampScan = res.find(
              (x) => x.type === 'stamps_per_scan',
            );
            const restrictionStampLimit = res.find(
              (x) => x.type === 'stamp_scan_limit',
            );

            if (employment.role !== 'restricted' && restrictionStampLimit) {
              setMaxStampIssued(
                parseInt(restrictionStampLimit.restriction[0], 10),
              );
            } else {
              setMaxStampIssued(20);
            }

            if (employment.role === 'restricted' && restrictionStampScan) {
              setRestrictedStamp(
                parseInt(restrictionStampScan.restriction[0], 10),
              );
            }

            setInit(false);
          }
        } catch (e) {
          console.log('Error:', e);
          setRestrictionFetchFailed(true);
          setInit(false);
        }
      }
    };
    if (employment && init) {
      fetchRestrictionList();
    }
  }, [employment, init]);

  const handleLogOut = async () => {
    auth?.removeSession();
    nav?.removeNav();
    selectedStore?.removeSelectedStore();
    history.replace({});
    history.push(`/`);
  };

  const handleRetryBootstrap = () => {
    window.location.reload();
  };

  if (profileFetchFailed || employmentFetchFailed || restrictionFetchFailed) {
    return (
      <div className={classes.noNetworkContainer}>
        <Typography variant="h5" className={classes.noNetworkHeader}>
          Network Error
        </Typography>
        <WifiOffIcon color="disabled" className={classes.wifiOff} />
        <Typography className={classes.noNetworkSubText}>
          Please check your connection and tap REFRESH to try again.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetryBootstrap}
        >
          REFRESH
        </Button>
      </div>
    );
  }

  if (!profile || !employment || init) {
    return (
      <div className={classes.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  if (stampCampaignList.length === 0) {
    return (
      <div className={classes.noNetworkContainer}>
        <Typography variant="h5" className={classes.noNetworkHeader}>
          Network Error
        </Typography>
        <WifiOffIcon color="disabled" className={classes.wifiOff} />
        <Typography className={classes.noNetworkSubText}>
          Please check your connection and tap REFRESH to try again.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetryBootstrap}
        >
          REFRESH
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className={classes.grow}>
        <div className={classes.container}>
          {selectedStore &&
            selectedStore.selectedStore &&
            selectedStore.selectedStore.storeId &&
            profile && (
              <QRCodeOnlyScreen
                onLogout={() => {
                  handleLogOut();
                }}
                stampNum={restrictedStamp}
                storeId={selectedStore.selectedStore.storeId}
                onlyScanOnce={false}
                maxStampIssued={maxStampIssued}
                stampCampaignList={stampCampaignList}
                storeName={selectedStore.selectedStore.storeName}
              />
            )}
          <SelectStoreDialog
            employment={employment}
            profile={profile}
            open={selectStoreDialogOpen}
            onClose={() => setSelectStoreDialogOpen(false)}
            onSuccess={(store: StoreService.Store) => {
              selectedStore?.setSelectedStore({
                storeId: store.id,
                storeName: store.name,
              });
              setSaveToHomeDialog(true);
            }}
          />
          <SaveToHomeScreenDialog
            open={saveToHomeDialog}
            onClose={() => setSaveToHomeDialog(false)}
          />
        </div>
      </div>
    </>
  );
}
